import { useState, useRef, useEffect } from "react";

const ScreenSave = () => {
  const containerRef = useRef(null);
  const floaterRef = useRef(null);
  const [position, setPosition] = useState({
    x: Math.floor(Math.random() * (420 - 10 + 1)) + 10,
    y: Math.floor(Math.random() * (420 - 10 + 1)) + 10,
  });
  const [color, setColor] = useState("#FF5733");
  const [velocity, setVelocity] = useState({ dx: 1.5, dy: 1.5 }); // movement speed
  const animationRef = useRef(null);

  useEffect(() => {
    const colors = [
      // Original vibrant colors
      "#FF5733",
      "#33FF57",
      "#3357FF",
      "#F1C40F",
      "#9B59B6",
      "#1ABC9C",
      "#E67E22",
      "#E74C3C",
      "#2ECC71",
      "#3498DB",

      // Neon & Electric
      "#39FF14",
      "#00FFFF",
      "#FF00FF",
      "#7DF9FF",
      "#FE019A",
      "#B026FF",
      "#FF3131",
      "#CCFF00",
      "#FF6EC7",
      "#00FF9F",

      // Ocean & Cool Tones
      "#006994",
      "#0077B6",
      "#00B4D8",
      "#48CAE4",
      "#023E8A",
      "#4361EE",
      "#3A0CA3",
      "#7209B7",
      "#4CC9F0",
      "#80FFDB",

      // Sunset & Warm Tones
      "#FF6B35",
      "#FF9F1C",
      "#FFBF69",
      "#FF4D6D",
      "#C9184A",
      "#FF758F",
      "#E63946",
      "#F77F00",
      "#FCBF49",
      "#D62828",

      // Purple & Dreamy
      "#845EC2",
      "#D65DB1",
      "#FF6F91",
      "#AA96DA",
      "#C3B1E1",
      "#6A4C93",
      "#9D4EDD",
      "#E0AAFF",
      "#C77DFF",
      "#5A189A",

      // Fresh Greens
      "#38B000",
      "#70E000",
      "#9EF01A",
      "#008000",
      "#06D6A0",
      "#52B788",
      "#95D5B2",
      "#2D6A4F",
      "#B7E4C7",
      "#D9ED92",

      // Extra premium shades
      "#F8F9FA",
      "#ADB5BD",
      "#6C757D",
      "#343A40",
      "#FFEA00",
      "#FF007F",
      "#00A6FB",
      "#F72585",
      "#3F37C9",
      "#4D908E",
    ];

    const move = () => {
      const container = containerRef.current;
      const floater = floaterRef.current;
      if (!container || !floater) return;

      const containerRect = container.getBoundingClientRect();
      const floaterRect = floater.getBoundingClientRect();

      let newX = position.x + velocity.dx;
      let newY = position.y + velocity.dy;

      // bounce on X axis
      if (newX <= 0 || newX + floaterRect.width >= containerRect.width) {
        const random_color = Math.floor(Math.random() * colors.length);
        setColor(colors[random_color]);
        setVelocity((prev) => ({ ...prev, dx: -prev.dx }));
        newX = Math.max(
          0,
          Math.min(newX, containerRect.width - floaterRect.width),
        );
      }

      // bounce on Y axis
      if (newY <= 0 || newY + floaterRect.height >= containerRect.height) {
        let random_color = Math.floor(Math.random() * colors.length);
        while (colors[random_color] === color) {
          //make sure color isn't selected twice in a row
          random_color = Math.floor(Math.random() * colors.length);
        }
        setColor(colors[random_color]);
        setVelocity((prev) => ({ ...prev, dy: -prev.dy }));
        newY = Math.max(
          0,
          Math.min(newY, containerRect.height - floaterRect.height),
        );
      }

      setPosition({ x: newX, y: newY });

      animationRef.current = requestAnimationFrame(move);
    };

    animationRef.current = requestAnimationFrame(move);
    return () => cancelAnimationFrame(animationRef.current);
  }, [position, velocity]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100vw",
        height: "100vh",
        zIndex: 99999,
        backgroundColor: "black",
        position: "absolute",
        overflow: "hidden",
      }}
    >
      <div
        ref={floaterRef}
        style={{
          width: "80px",
          height: "80px",
          position: "absolute",
          left: position.x,
          top: position.y,
        }}
      >
        <svg
          version="1.1"
          viewBox="0 0 587.43 541.63"
          xmlns="http://www.w3.org/2000/svg"
          style={{ filter: `drop-shadow(0 0 6px ${color})` }}
        >
          <g transform="translate(-211.75 -405.75)">
            <path
              transform="translate(597.86 435.18)"
              d="m0 0c6.9426 6.3155 13.884 12.86 18.637 21.012 4.4662 7.2787 8.2633 12.541 16.746 15.02 5.8139 1.3476 11.689 2.295 17.583 3.2212 27.595 4.9011 54.506 23.333 70.675 45.626 11.08 16.075 16.706 34.447 16.688 54l2e-3 2.1931c-0.0704 7.3518-1.0074 14.494-2.1894 21.744l2.2031 0.44532c21.059 4.6979 38.121 18.243 49.758 36.211 11.052 18.124 13.741 37.942 8.9417 58.582-2.945 11.503-8.2037 21.67-14.028 31.95-7.3735 13.071-12.313 24.41-9.293 39.773 1.0465 3.7272 2.3567 6.923 4.168 10.352 1.4439 3.1044 5.7069 17.398 4.67 20.603-8.2942-6.9116-28.686-13.39-39.533-19.119-21.37-13.302-33.514-38.283-39.887-61.797l-1.0625-3.6875c-1.3609-5.5186-1.65-11.159-2.0625-16.812l-0.26172-3.3594c-0.2105-2.7131-0.41477-5.4266-0.61328-8.1406-0.68351 3.4344-1.3436 6.8729-2 10.312l-0.57812 2.9004c-2.5427 13.486-1.8883 25.47 4.5781 37.787v4c-12.252-1.3988-24.037-6.2719-32-16-9.2696-13.832-11.506-29.445-13.438-45.625-1.631-13.573-4.434-26.08-12.562-37.375l-1.2227-1.8086c-6.827-9.04-20.419-17.75-30.777-22.191-4.0672 0.0784-6.1643 2.831-8.9375 5.5625-12.637 11.899-27.642 19.237-45.132 18.823-11.531-0.52603-20.145-3.9948-29.618-10.573-5.7973-3.9633-11.271-5.4412-18.312-4.8125-5.2885 1.7927-9.432 3.8641-12 9-1.0126 7.6527 0.57186 12.786 5 19 1.9509 2.4692 3.9458 4.8989 5.9414 7.332 8.4056 10.894 12.654 21.742 12.559 35.48l-0.01733 2.9128c-0.32811 13.121-5.1151 22.498-14.1 31.982-10.986 10.382-26.92 12.616-41.383 13.293 0.30675-3.3048 0.75041-4.7382 3.0742-7.1758l2.6133-2.1367c7.4523-6.4303 13.388-13.198 14.629-23.25 0.71911-16.331-9.5834-28.624-19.132-40.763-12.039-15.357-20.624-31.107-20.497-50.987l0.0171-2.7942c0.30123-16.957 6.572-27.934 18.295-39.893l2.0156-2.125c11.228-10.97 24.623-15.266 40.023-15.16 10.102 0.72729 19.585 4.7013 27.961 10.285-0.89114-1.378-1.7882-2.7523-2.6875-4.125l-1.5117-2.3203c-6.4979-9.2183-14.938-14.419-25.926-17.055-15.751-2.3163-29.158 3.7972-41.875 12.5l-2.6562 1.8047c-15.934 11.749-29.014 32.905-32.09 52.496-1.4044 14.93 0.0648 28.048 8.7461 40.699l1.9594 2.4461c12.552 13.826 15.815 30.882 15.349 48.96-0.91144 18.002-8.7738 33.974-21.801 46.272-3.51 3.0758-7.3249 5.2999-11.508 7.3213l-3.25 1.75c-15.786 5.262-33.656 4.6233-48.852-2.1367-8.1494-4.536-14.365-10.858-17.648-19.613-0.37553-3.0042-0.30813-5.9774-0.25-9 2.4728 1.099 4.2487 2.2151 6.3125 4 6.3805 4.7483 14 4.7932 21.688 4 6.2311-1.6168 11.703-4.1786 16-9 4.1552-9.2898 3.446-17.19 0.30078-26.68-3.7376-9.5405-10.03-16.294-17.022-23.549-13.354-14.076-21.925-31.383-21.716-51.021l0.0208-3.0325c0.1813-8.4382 1.52-15.749 4.4168-23.718l1.0976-3.1211c3.4539-9.0036 8.5729-16.885 13.902-24.879-13.026 5.9618-13.026 5.9618-24 15l-1.5938 1.6055c-11.226 12.056-15.692 28.623-15.562 44.747 0.27396 6.4162 1.1875 12.733 2.0933 19.085 3.8714 27.647 7.7468 56.134-8.9024 80.43-7.6037 9.9275-17.485 17.366-29.035 22.133l-2.668 1.1875c-15.178 5.7743-34.502 4.5812-49.355-1.4062-7.5096-3.4049-13.208-7.94-18.977-13.781l-1.707-1.6406c-8.303-8.6754-13.181-21.235-13.543-33.109 0.32111-11.524 2.0009-23.062 9.8359-32.074 2.5448-2.2542 5.008-3.7828 8.4141-4.1758 1 3 1 3 0.0625 6.25-2.184 8.2205-2.5725 17.567 1.0625 25.375 2.7919 4.687 5.5148 8.3819 10.977 9.9961 5.5368 0.92279 10.01 0.12323 14.898-2.6211 4.807-3.7145 7.537-8.1557 9-14 0.34154-2.815 0.45612-5.6033 0.5-8.4375l0.0559-2.0947c-0.11548-9.7819-5.228-17.97-9.9934-26.218-11.248-20.015-16.169-41.449-10.562-64.25 3.0672-9.4271 7.1582-17.946 13-26l2.0234-2.8047c10.96-14.284 27.857-24.643 45.73-27.66 2.0762-0.23718 4.1611-0.39615 6.2461-0.53516l-0.6211-2.0898c-1.7152-5.9825-3.0087-11.695-3.3789-17.91l-0.16016-2.1016c-0.62608-14.972 5.8085-28.65 15.68-39.605 1.4876-1.4865 0.77796-0.84136 2.355-2.3682l1.0681-0.96774c10.055-8.6594 21.22-13.118 34.307-13.269l2.4883-0.08789c5.6339-0.05804 10.798 0.67881 15.262 4.4004 0.77832 2.0769 0.77832 2.0769 1 4-1.9028 1.9028-5.3895 1.4954-7.9375 1.75-6.8562 0.78962-12.1 2.7066-18.062 6.25l-2.2879 1.7665c-2.1789 2.16-1.9363 1.91-3.7121 4.2335l-1.0352 2.0352-1.0273 2.0273-1.0352 2.0352c-2.6223 5.5284-2.438 10.651-0.83984 16.465 1.7566 4.8701 4.5705 7.7337 8.9375 10.438 2.4088 0.99844 2.4088 0.99844 5 1l3.6875 0.25c13.078-0.24537 23.322-7.6509 32.205-16.591 6.5882-6.9321 11.997-14.77 17.556-22.527 23.279-31.998 57.571-61.31 97.61-68.832l1.9414-0.30078 1.9727-0.33203c31.834-4.4627 60.371 7.7022 83.886 28.515z"
              fill={color}
            />
            <path
              transform="translate(554,462)"
              d="m0 0c13.508 9.4972 21.326 21.25 28 36 0.30631 0.6716 0.61263 1.3432 0.92822 2.0352 5.0718 11.201 5.0718 11.201 5.0718 14.965-4.1769-1.5542-6.424-4.6328-9.125-8-13.073-15.782-30.721-32.529-51.875-36-26.131-2.2468-52.328 5.6894-73.258 21.73-1.7422 1.2695-1.7422 1.2695-3.7422 1.2695 5.5588-16.171 23.157-31.499 38-39 1.03-0.52594 2.0599-1.0519 3.1211-1.5938 20.797-8.9881 44.527-3.1514 62.879 8.5938z"
              fill="#fbfbfb"
            />
            <path
              transform="translate(669,546)"
              d="m0 0c15.572 3.6175 28.906 13.138 37.594 26.559 6.1013 10.593 5.8506 23.431 3.0938 35.129-1.1677 4.0426-2.5683 7.6796-4.6875 11.312h-2c-0.06731-0.95084-0.06731-0.95084-0.13599-1.9209-1.6539-22.681-4.9632-45.286-23.192-61.099-2.1868-1.7197-4.411-3.3601-6.6719-4.9805-1.67-1.3292-3.3386-2.6601-5-4l1-1z"
              fill="#f9faf9"
            />
          </g>
          <path
            d="m196.94 373.68c0.81293 0.059 1.6258 0.1174 2.4634 0.1778 22.167 1.6577 42.619 5.3664 62.787 14.947 18.976 9.002 35.879 15.759 56.65 8.544 6.9653-2.6125 13.634-5.857 20.35-9.044 18.739-8.8471 36.513-12.034 57.062-13.938 1.0477-0.1044 2.0954-0.2088 3.1748-0.3164 37.473-3.6223 76.488-3.7198 113.83 1.3164 1.1563 0.1552 2.3126 0.3104 3.5039 0.4702 3.2957 0.4598 6.5839 0.9509 9.8711 1.4673 0.92732 0.1454 1.8546 0.2907 2.8101 0.4405 9.7433 1.6977 18.205 3.7831 24.573 11.72 3.4569 5.294 2.5034 11.9 2.5374 17.989 0.0115 1.4353 0.0319 2.8706 0.0615 4.3056 0.30044 14.572 0.30044 14.572-4.3567 20.608-3.0745 4.1711-4.6193 7.4271-5.5273 12.524-0.34829 1.8856-0.34829 1.8856-0.70362 3.8093-0.11523 0.6595-0.23046 1.3191-0.34918 1.9986-0.36258 2.0735-0.74201 4.1433-1.1264 6.2128-0.52114 2.8079-1.0374 5.6166-1.5498 8.426-6.8221 37.192-6.8221 37.192-15.744 49.03-0.5775 0.8663-1.155 1.7325-1.75 2.625-18.068 19.072-48.542 22.85-73.405 23.608-34.558 0.8328-74.067-1.7915-100.49-26.725-18.949-19.588-28.516-48.079-34.735-74.001-0.61545-2.61-0.61545-2.61-1.6243-5.5071-4.9975-1.9123-9.6596-2.3113-15-2.25-0.67289-0.01-1.3458-0.015-2.0391-0.023-4.624 0.018-8.6664 0.5296-12.961 2.2735-1.2796 1.896-1.2796 1.896-1.5625 4.375-6.7767 31.271-19.629 66.487-47.125 85.312-36.511 21.164-88.573 21.039-128.44 11.062-14.697-4.0093-29.533-11.03-37.645-24.504-0.60908-1.1118-0.60908-1.1118-1.2305-2.2461-0.3545-0.6277-0.70899-1.2555-1.0742-1.9023-7.7228-15.415-9.5376-34.129-12.427-50.932-0.21453-1.22-0.21453-1.22-0.43339-2.4647-0.26921-1.5318-0.53168-3.0649-0.78592-4.5993-1.1011-6.2136-2.9807-11.796-6.9045-16.851-4.4647-5.7905-3.6312-12.348-3.6702-19.336-0.0115-1.4353-0.0319-2.8706-0.0615-4.3057-0.30467-14.777-0.30467-14.777 4.3567-20.608 27.135-27.766 113.97-16.237 148.69-13.688zm-110.94 33.812c-8.6579 10.87-10.127 24.65-10.062 38 5e-3 1.1991 0.01 2.3982 0.0149 3.6336 0.25512 19.317 4.9963 40.465 18.548 55.054 21.1 19.186 52.793 20.301 79.75 19.188 6.1138-0.4876 12.041-1.5725 18-3 1.0695-0.2494 1.0695-0.2494 2.1606-0.5039 15.527-3.7439 28.524-10.037 39.839-21.496 0.8366-0.7863 1.6732-1.5726 2.5352-2.3828 15.244-16.186 25.063-46.082 24.598-68.004-0.34822-6.8518-0.73819-11.838-5.8203-16.863-27.592-21.827-76.654-18.891-109.69-19-0.81331-0.01-1.6266-0.011-2.4646-0.016-19.887-0.1-42.694 0.078-57.41 15.391zm258.12 4.5c-5.1683 6.5465-5.4945 14.22-4.875 22.375 3.0334 23.407 11.846 52.845 30.777 68.348 26.122 19.42 59.325 23.984 91.067 19.827 18.108-2.7997 35.622-8.1396 48.156-22.175 14.446-20.342 17.62-46.183 14.695-70.537-1.8896-10.846-6.657-20.924-15.301-27.932-15.831-10.123-35.582-9.7422-53.645-9.7188-0.85871 6e-4 -1.7174 1e-3 -2.6021 0-57.363-1.5586-57.363-1.5586-108.27 19.81zm-288.87-8.625v8l9-4c-2.6856-2.6855-5.3762-3.0605-9-4zm481 3c2.5296 3.0917 5.2246 3.9093 9 5v-8c-3.3036 0-6.0211 1.6761-9 3z"
            fill={color}
          />
        </svg>
        ;
      </div>
    </div>
  );
};

export default ScreenSave;
